import type { ReactNode } from 'react'
import BottomSheetContent from '../../../../../../../components/BottomSheetComponents/BottomSheetContent'
import RenderHTML from '../../../../../../../components/ui-components/RenderHTML'

const MANUAL_BOOK_ICON = `<svg width="48" height="48" viewBox="0 0 38.4286 45.0519" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M14.2468 12.7688C14.2468 11.7863 14.5381 10.8259 15.0839 10.009C15.6298 9.19206 16.4056 8.55535 17.3133 8.17938C18.221 7.8034 19.2198 7.70504 20.1834 7.89669C21.147 8.08837 22.0321 8.56148 22.7269 9.2562C23.4216 9.95093 23.8947 10.836 24.0864 11.7997C24.2781 12.7633 24.1797 13.7621 23.8037 14.6698C23.4277 15.5775 22.791 16.3533 21.9741 16.8992C21.1572 17.445 20.1968 17.7363 19.2143 17.7363V19.3922" stroke="#232323" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M19.2143 26.2225C18.757 26.2225 18.3864 25.8518 18.3864 25.3946C18.3864 24.9373 18.757 24.5667 19.2143 24.5667" stroke="#232323" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M19.2143 26.2225C19.6715 26.2225 20.0422 25.8518 20.0422 25.3946C20.0422 24.9373 19.6715 24.5667 19.2143 24.5667" stroke="#232323" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M37.4286 44.052H5.96753C4.65008 44.052 3.38654 43.5287 2.45496 42.5971C1.52338 41.6655 1 40.4018 1 39.0844C1 37.767 1.52338 36.5033 2.45496 35.5717C3.38654 34.6401 4.65008 34.1169 5.96753 34.1169H34.1169C34.9951 34.1169 35.8376 33.7678 36.4586 33.1469C37.0795 32.526 37.4286 31.6835 37.4286 30.8052V4.31169C37.4286 3.43336 37.0795 2.59103 36.4586 1.96997C35.8376 1.34891 34.9951 1 34.1169 1H5.96753C4.67269 0.99954 3.42886 1.50468 2.50099 2.40782C1.57309 3.31096 1.03454 4.54069 1 5.83506V38.952" stroke="#232323" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M34.1169 34.1169V44.0519" stroke="#232323" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`

type StaticInfoBottomSheetProps = {
  onDismiss: () => void
}

export function RankOneNotRecommendedInfoBottomSheet({ onDismiss }: StaticInfoBottomSheetProps) {
  return (
    <ImportantNoteStaticInfoBottomSheet
      heading="Why is Rank 1 not always recommended?"
      headingClassName="!font-[400]"
      onDismiss={onDismiss}
    >
      <div className="text-[14px] font-[400] leading-[150%] text-[#6A6E6E]">
        <p>When we suggest a switch, we don&apos;t just look at the fund&apos;s current rank.</p>
        <p className="pt-6">
          We simulate your exact investment journey — every SIP, lump sum, or withdrawal — against all
          top-ranked funds in the same category. The fund that delivers the best outcome for you (in terms
          of XIRR + missed gains) is the one we recommend.
        </p>
        <p className="pt-6">That&apos;s why recommendations can differ from person to person.</p>
        <p className="pt-6">
          All funds ranked 1, 2, and 3 in a category are strong performers and suitable for switching.
        </p>
        <p className="pt-6">
          If you prefer a specific Fund House, you&apos;re free to pick any among the Top 3 ranked funds in
          the same category.
        </p>
      </div>
    </ImportantNoteStaticInfoBottomSheet>
  )
}

export function GrowthPlanInsteadOfIdcwInfoBottomSheet({ onDismiss }: StaticInfoBottomSheetProps) {
  return (
    <ImportantNoteStaticInfoBottomSheet
      heading="Why we recommend investing in a Growth plan, instead of IDCW"
      headingClassName="!font-[500]"
      onDismiss={onDismiss}
    >
      <div className="flex flex-col gap-6 text-[14px] font-[400] leading-[150%] text-[#6A6E6E]">
        <p>Here&apos;s why Growth plans are better, compared to IDCW :</p>
        <div>
          <p className="font-[500] text-[#6A6E6E]">Your money compounds without interruptions</p>
          <p className="pt-1">
            In Growth plans, all gains stay invested, helping your investment compound more over time.
            Whereas in IDCW plans, payouts reduce your invested amount, hindering your long term
            compounding
          </p>
        </div>
        <div>
          <p className="font-[500] text-[#6A6E6E]">More tax-efficient for long-term investors</p>
          <p className="pt-1">
            You are taxed only at the time of redemption, unlike the payouts received from IDCW funds -
            which are taxable as per your income tax bracket, every time the fund distributes cash.
          </p>
        </div>
        <p>
          If you prefer regular cashflows, you can opt for an IDCW plan. But for long-term wealth
          creation, we recommend switching to the Direct Growth plan of the fund
        </p>
      </div>
    </ImportantNoteStaticInfoBottomSheet>
  )
}

export function DirectFundsInsteadOfRegularInfoBottomSheet({ onDismiss }: StaticInfoBottomSheetProps) {
  return (
    <ImportantNoteStaticInfoBottomSheet
      heading="Why we recommend investing in Direct funds, instead of Regular."
      headingClassName="!font-[500]"
      onDismiss={onDismiss}
    >
      <div className="flex flex-col gap-6 text-[14px] font-[400] leading-[150%] text-[#6A6E6E]">
        <p>
          Mutual funds come in two plans, Regular and Direct. They&apos;re the same fund, but Regular has
          higher expense ratio.
        </p>
        <div>
          <p className="font-[500] text-[#6A6E6E]">Direct funds have lower costs</p>
          <p className="pt-1">
            Regular funds include commissions, which are charged through a higher expense ratio. Direct
            plans don&apos;t have these commissions, so more of your money stays invested.
          </p>
        </div>
        <p>
          That&apos;s why PowerUp Money recommends investing in Direct mutual fund plans wherever possible.
        </p>
      </div>
    </ImportantNoteStaticInfoBottomSheet>
  )
}

export function ElssRebalancingInfoBottomSheet({ onDismiss }: StaticInfoBottomSheetProps) {
  return (
    <ImportantNoteStaticInfoBottomSheet
      heading="Here's how to go about rebalancing for ELSS funds:"
      headingClassName="!font-[400]"
      onDismiss={onDismiss}
    >
      <ul className="list-disc space-y-4 pl-5 text-[14px] font-[400] leading-[150%] text-[#6A6E6E] marker:text-[#6A6E6E]">
        <li>
          The rebalance recommendation is for your entire fund value, not just the unlocked units.
        </li>
        <li>
          You can only switch the units that have completed the 3-year lock-in period. As more units
          complete 3 years, switch them based on the rebalancing recommendation.
        </li>
        <li>
          There&apos;s currently no way to rebalance the locked units before the lock-in period ends.
        </li>
      </ul>
    </ImportantNoteStaticInfoBottomSheet>
  )
}

export function SwitchOnTrackFundInfoBottomSheet({ onDismiss }: StaticInfoBottomSheetProps) {
  return (
    <ImportantNoteStaticInfoBottomSheet
      heading="Why switch an On-track fund?"
      headingClassName="!font-[400]"
      onDismiss={onDismiss}
    >
      <div className="text-[14px] font-[400] leading-[150%] text-[#6A6E6E]">
        <p>We recommend upgrading an on-track fund only when its highly beneficial.</p>
        <p className="pt-6">
          This is to help you{' '}
          <span className="font-[500] text-[#232323]">maximise your returns, </span>
          which is one of the key goals of your Power Rebalancing plan while keeping your risk the same.
        </p>
      </div>
    </ImportantNoteStaticInfoBottomSheet>
  )
}

export function ImportantNoteStaticInfoBottomSheet({
  heading,
  headingClassName,
  onDismiss,
  children
}: {
  heading: string
  headingClassName: string
  onDismiss: () => void
  children: ReactNode
}) {
  return (
    <BottomSheetContent
      heading={heading}
      logoSvg={<RenderHTML innerHTMLText={MANUAL_BOOK_ICON} className="size-12 shrink-0" />}
      onDismiss={onDismiss}
      bottomSheetHeadClassName="!pt-6"
      bottomSheetHeadingClassName={`!text-[22px] !text-[#232323] !leading-[130%] !mb-0 ${headingClassName}`}
      bottomSheetBodyClassName="!pt-3 !pb-0"
    >
      {children}
    </BottomSheetContent>
  )
}
